import { api } from './client'

export interface GitLabConfig {
  tenant_id: number
  gitlab_url: string
  gitlab_group_url?: string
  default_branch: string
  sync_enabled: boolean
  gitlab_project_id?: number
  gitlab_project_ids?: number[]
}

export interface Pipeline {
  id: number
  status: string
  ref: string
  sha?: string
  commit_message?: string
  duration?: number
  created_at: string
  updated_at: string
  web_url: string
  project_id?: number
  project_path?: string
}

export interface MergeRequest {
  id: number
  iid: number
  title: string
  state: string
  created_at: string
  updated_at: string
  merged_at?: string
  web_url: string
  author: { name: string }
  target_branch: string
  source_branch: string
  has_conflicts?: boolean
  labels?: string[]
  project_id?: number
  project_path?: string
}

export interface ActivityEvent {
  type: string
  timestamp: string
  title: string
  status: string
  project_id?: number
  project_path?: string
}

export interface MetricsSummary {
  deploys_30d: number
  deploy_freq_per_week: number
  trend_pct: number
  lead_time_hours: number
  deploy_history: Array<{ week_label: string; count: number }>
  degraded?: boolean
}

/**
 * `degraded: true` = a leitura no GitLab estourou o deadline (10s de dashboard,
 * 6s por chamada) e a api respondeu 200 com a resposta ZERADA. Os zeros e as
 * listas vazias que vem junto NAO sao medicao — sao ausencia de leitura.
 *
 * O campo existia no JSON desde o incidente de 10/06/2026 e nao existia AQUI,
 * entao o front descartava o aviso e a tela exibia "0 pipelines" como fato
 * (16 respostas degradadas em 8h18 no dia 18/09/2026). TASK-10996.
 *
 * A chave vem AUSENTE quando a leitura foi bem — nunca `degraded: false`.
 */
export interface PipelinesResponse {
  pipelines: Pipeline[]
  count: number
  branch: string
  degraded?: boolean
}

export interface MergeRequestsResponse {
  merge_requests: MergeRequest[]
  count: number
  degraded?: boolean
}

export interface ActivityResponse {
  events: ActivityEvent[]
  count: number
  degraded?: boolean
}

/** Resposta de `GET /gitlab/metrics` (a TeamTab), achatada e sem serie temporal. */
export interface TeamMetrics {
  activity: number
  deployment_frequency: number
  velocity: number
  lead_time_hours: number
  period: string
  degraded?: boolean
}

export const gitlabApi = {
  getConfig: (tenantId: number) =>
    api.get<GitLabConfig>(`/v1/tenants/${tenantId}/gitlab/config`).then(r => r.data),

  saveConfig: (tenantId: number, data: { gitlab_group_url: string; default_branch?: string; enabled: boolean }) =>
    api.put(`/v1/admin/tenants/${tenantId}/gitlab/config`, data).then(r => r.data),

  disableConfig: (tenantId: number) =>
    api.put(`/v1/admin/tenants/${tenantId}/gitlab/config`, { enabled: false }).then(r => r.data),

  getPipelines: (tenantId: number, branch?: string) => {
    const params = branch ? `?branch=${branch}` : ''
    return api.get<PipelinesResponse>(`/v1/tenants/${tenantId}/gitlab/pipelines${params}`).then(r => r.data)
  },

  getMergeRequests: (tenantId: number, branch?: string) => {
    const params = branch ? `?branch=${branch}` : ''
    return api.get<MergeRequestsResponse>(`/v1/tenants/${tenantId}/gitlab/merge-requests${params}`).then(r => r.data)
  },

  getActivity: (tenantId: number) =>
    api.get<ActivityResponse>(`/v1/tenants/${tenantId}/gitlab/activity`).then(r => r.data),

  getMetrics: (tenantId: number) =>
    api.get<MetricsSummary>(`/v1/tenants/${tenantId}/gitlab/metrics/summary`).then(r => r.data),

  getTeamMetrics: (tenantId: number, period: 'week' | 'month' | 'quarter' = 'month') =>
    api.get<TeamMetrics>(`/v1/tenants/${tenantId}/gitlab/metrics?period=${period}`).then(r => r.data),

  sync: (tenantId: number) =>
    api.post(`/v1/tenants/${tenantId}/gitlab/sync`, {}).then(r => r.data),
}
